/**
 * Empty-paragraph slash-hint extension (Phase 4a polish).
 *
 * Decorates every empty paragraph in the document — EXCEPT the first
 * child (TipTap's Placeholder extension already handles that one) —
 * with a `data-slash-hint` attribute carrying a localized "Type / to
 * insert a block" string. The pure-CSS rule in `global.css` reads the
 * attribute (`content: attr(data-slash-hint)`) and renders the hint as
 * a `::before` pseudo-element when the user focuses the paragraph.
 *
 * Why an extension and not a hardcoded CSS string? The hint is user-
 * facing copy + must localize through the i18n catalog. Routing the
 * translated string into the DOM via a `data-` attribute keeps the
 * rendering rule in CSS (where it belongs) while letting the
 * translation come from `t('wiki.slashMenu.emptyParagraphHint')` at the
 * call site (`DocPage.tsx`). The pattern mirrors the
 * tiptap-extension-placeholder approach but is narrower in scope: we
 * only decorate empty paragraphs, not every empty block kind, because
 * the slash menu's `allow` predicate is paragraph-scoped (see
 * `slash-command-extension.ts:isSlashMenuAllowed`).
 *
 * Caller surface: wiki editor only. Other editor surfaces never load
 * the extension, so card / comment / chat composers keep their
 * existing "first-paragraph-only" placeholder UX unchanged.
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Extension } from '@tiptap/react'

export interface EmptyParagraphHintOptions {
  /**
   * Function returning the localized hint string. Read on every plugin
   * tick so a locale switch re-emits decorations with fresh copy without
   * tearing down the editor — mirrors the `getItems` pattern used by
   * the mention + wikilink suggestion configs.
   */
  getTranslation: () => string
}

const EMPTY_PARAGRAPH_HINT_KEY = new PluginKey('emptyParagraphHint')

/**
 * Build the decoration set for one editor state. Walks the top-level
 * doc children, finds every empty paragraph that is NOT the first
 * child, and attaches a node-decoration carrying `data-slash-hint`.
 *
 * Why skip the first child: the wiki editor already mounts TipTap's
 * `Placeholder` extension scoped to the first paragraph with its own
 * (translated) placeholder copy. Decorating the same node twice would
 * double-render the hint.
 *
 * Why node-level decoration (not widget): the hint is a CSS pseudo-
 * element on the paragraph itself — the attribute lives on the
 * paragraph's DOM node, not a sibling.
 */
function buildDecorations(doc: ProseMirrorNode, hint: string): DecorationSet {
  const decorations: Decoration[] = []
  doc.forEach((child, offset, index) => {
    if (child.type.name !== 'paragraph') return
    if (child.content.size > 0) return
    // First top-level child is the Placeholder extension's surface;
    // skip to avoid double rendering.
    if (index === 0) return
    decorations.push(
      Decoration.node(offset, offset + child.nodeSize, {
        'data-slash-hint': hint,
      }),
    )
  })
  return DecorationSet.create(doc, decorations)
}

export const EmptyParagraphHintExtension = Extension.create<EmptyParagraphHintOptions>({
  name: 'emptyParagraphHint',

  addOptions() {
    return {
      // Default to empty so the extension is a no-op if loaded without
      // a translation getter; CSS rule won't render anything visible
      // since `content: attr(data-slash-hint)` resolves to "".
      getTranslation: () => '',
    }
  },

  addProseMirrorPlugins() {
    const getTranslation = () => this.options.getTranslation()
    return [
      new Plugin({
        key: EMPTY_PARAGRAPH_HINT_KEY,
        props: {
          decorations(state) {
            return buildDecorations(state.doc, getTranslation())
          },
        },
      }),
    ]
  },
})
